
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DragDropContext } from "@hello-pangea/dnd";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { LeadStageColumn } from "./board/LeadStageColumn";
import { useState, useEffect } from "react";
import { LeadToProjectDialog } from "./conversion/LeadToProjectDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { useOrganization } from "@/contexts/OrganizationContext";

const LEAD_STAGES = [
  { value: "new", label: "New" },
  { value: "contact_established", label: "Contact Established" },
  { value: "first_meeting", label: "First Meeting" },
  { value: "negotiating", label: "Negotiating" },
  { value: "closed_won", label: "Closed Won" },
  { value: "closed_lost", label: "Closed Lost" },
  { value: "on_hold", label: "On Hold" },
];

export const LeadBoard = () => {
  const { toast } = useToast();
  const [collapsedStages, setCollapsedStages] = useState<Record<string, boolean>>({});
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [leadToConvert, setLeadToConvert] = useState<any>(null);
  const isMobile = useIsMobile();
  const { currentOrganization } = useOrganization();
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["leads", currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization) throw new Error("No organization selected");
      
      // Direct database query with relationships - much faster than edge function
      const { data, error } = await supabase
        .from('leads')
        .select(`
          *,
          contacts:contact_id (
            name,
            email,
            company,
            phone
          ),
          project:project_id (
            name,
            price,
            pricing_type,
            payment_date,
            recurring_start_date,
            recurring_end_date
          )
        `)
        .eq('organization_id', currentOrganization.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Fetch events separately for better performance
      if (data?.length > 0) {
        const leadIds = data.map(lead => lead.id);
        const { data: events } = await supabase
          .from('lead_events')
          .select('*')
          .in('lead_id', leadIds)
          .order('created_at', { ascending: false });
        
        // Map events to leads
        return data.map(lead => ({
          ...lead,
          events: events?.filter(event => event.lead_id === lead.id) || []
        }));
      }
      
      return data || [];
    },
    enabled: !!currentOrganization,
  });

  useEffect(() => {
    if (!currentOrganization) return;

    let isVisible = true;
    const abortController = new AbortController();

    // Handle visibility changes to pause/resume subscriptions
    const handleVisibilityChange = () => {
      isVisible = !document.hidden;
      if (isVisible) {
        // Refetch when becoming visible again
        setTimeout(() => refetch(), 100);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const channelName = `leads_org_${currentOrganization.id}`;
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'leads',
        filter: `organization_id=eq.${currentOrganization.id}`
      }, (payload) => {
        // Only process updates if page is visible
        if (isVisible && !abortController.signal.aborted) {
          console.info('Real-time leads update:', payload.eventType);
          refetch();
        }
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'lead_events',
        filter: `organization_id=eq.${currentOrganization.id}`
      }, (payload) => {
        // Only process updates if page is visible
        if (isVisible && !abortController.signal.aborted) {
          console.info('Real-time lead_events update:', payload.eventType);
          refetch();
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setIsSubscribed(true);
        }
      });

    return () => {
      abortController.abort();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      supabase.removeChannel(channel);
      setIsSubscribed(false);
    };
  }, [refetch, currentOrganization]);

  const getLeadsByStage = (stage: string) => {
    return data?.filter((lead) => lead.stage === stage) || [];
  };

  const handleDragEnd = async (result: any) => {
    const { destination, source, draggableId } = result;

    if (!destination || 
        (destination.droppableId === source.droppableId && 
         destination.index === source.index) ||
        !currentOrganization) {
      return;
    }

    const newStage = destination.droppableId;
    const lead = data?.find(l => l.id === draggableId);

    if (!lead) {
      console.error('Lead not found:', draggableId);
      return;
    }

    try {
      // Get the current user ID
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id; // This is now properly typed
      
      // Use the lead-operations edge function to update the lead stage
      const { data: updateData, error } = await supabase.functions.invoke('lead-operations', {
        body: {
          action: 'update',
          leadId: draggableId,
          lead: { stage: newStage },
          userId: userId,
          organizationId: currentOrganization.id
        },
      });

      if (error) throw error;

      console.log('Lead updated successfully:', updateData);

      if (newStage === 'closed_won' && lead && !lead.converted) {
        setLeadToConvert(lead);
      }

      toast({
        title: "Lead Updated",
        description: "Lead stage has been updated successfully",
      });

      refetch();
    } catch (error) {
      console.error('Error updating lead:', error);
      toast({
        title: "Error",
        description: "Failed to update lead stage",
        variant: "destructive",
      });
    }
  };

  const toggleStageCollapse = (stage: string) => {
    setCollapsedStages(prev => ({
      ...prev,
      [stage]: !prev[stage]
    }));
  };

  if (!currentOrganization) {
    return <div>Please select an organization to view leads.</div>;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[calc(100vh-12rem)]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <ScrollArea className="h-[calc(100vh-12rem)] w-full rounded-lg border">
          <div className={`${isMobile ? 'flex flex-col' : 'flex'} gap-4 p-4 ${isMobile ? 'w-full' : 'w-max'}`}>
            {LEAD_STAGES.map((stage) => (
              <LeadStageColumn
                key={stage.value}
                stage={stage}
                leads={getLeadsByStage(stage.value)}
                isCollapsed={collapsedStages[stage.value]}
                onToggleCollapse={toggleStageCollapse}
                onUpdate={() => refetch()}
                isMobile={isMobile}
              />
            ))}
          </div>
          <ScrollBar orientation={isMobile ? "vertical" : "horizontal"} />
        </ScrollArea>
      </DragDropContext>

      <LeadToProjectDialog
        lead={leadToConvert}
        open={!!leadToConvert}
        onOpenChange={(open) => !open && setLeadToConvert(null)}
        onSuccess={() => {
          setLeadToConvert(null);
          refetch();
        }}
      />
    </>
  );
};
